import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, FileText, Download, Search, Filter, TrendingUp, AlertTriangle,
  CheckCircle2, Clock, Mail, Eye, X, FileSpreadsheet, CreditCard, XCircle,
} from 'lucide-react';
import { RevenueChart } from './RevenueChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { id: 'current_month', label: 'Ce mois' },
  { id: '3m', label: '3 mois' },
  { id: '6m', label: '6 mois' },
  { id: '12m', label: '12 mois' },
  { id: 'all', label: 'Tout' },
];

/**
 * Body-Up Compta : Module facturation complet (remplace Bexio).
 * - KPI dashboard par période
 * - Liste factures avec recherche + filtres + téléchargement PDF
 * - Onglet "Relances" avec envoi MANUEL (jamais automatique)
 * - Export CSV pour la fiduciaire
 */
export const AdminBillingPanel = ({ csrfToken, onLogout, fmtDate, onGoToClients }) => {
  const [tab, setTab] = useState('overview'); // overview | invoices | reminders | stripe
  const [period, setPeriod] = useState('current_month');
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [reminderModal, setReminderModal] = useState(null);
  const [toast, setToast] = useState(null);
  // Compteur places VIP
  const [vipSpots, setVipSpots] = useState(null);   // null = masqué
  const [vipSpotsInput, setVipSpotsInput] = useState('');
  const [vipSpotsSaving, setVipSpotsSaving] = useState(false);

  const get = (url) => fetch(url, { credentials: 'include' });
  const post = (url, body) => fetch(url, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify(body || {}),
  });

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 5000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, iRes, rRes, vRes] = await Promise.all([
        get(`${BACKEND_URL}/api/admin/billing/stats?period=${period}`),
        get(`${BACKEND_URL}/api/admin/billing/invoices?period=${period}&limit=200${statusFilter ? `&status=${statusFilter}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
        get(`${BACKEND_URL}/api/admin/billing/reminders`),
        get(`${BACKEND_URL}/api/admin/billing/vip-spots`),
      ]);
      if (sRes.status === 401) { onLogout(); return; }
      setStats((await sRes.json()).kpi);
      setInvoices((await iRes.json()).invoices || []);
      setReminders((await rRes.json()).reminders || []);
      const vData = await vRes.json();
      const spots = vData.vip_spots_remaining;
      setVipSpots(spots);
      setVipSpotsInput(spots !== null && spots !== undefined ? String(spots) : '');
    } finally {
      setLoading(false);
    }
  }, [period, statusFilter, search, onLogout]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const downloadInvoicePdf = async (invoiceNumber) => {
    const res = await get(`${BACKEND_URL}/api/admin/billing/invoice/${invoiceNumber}/pdf`);
    if (!res.ok) { showToast('error', 'Erreur téléchargement PDF'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Facture_Body-Up_${invoiceNumber}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = async () => {
    const res = await get(`${BACKEND_URL}/api/admin/billing/export.csv?period=${period}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Body-Up_facturation_${period}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const saveVipSpots = async (value) => {
    // value = number (0-20) or null (masquer)
    setVipSpotsSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/billing/vip-spots`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ spots: value }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      const data = await res.json();
      setVipSpots(data.vip_spots_remaining);
      showToast('success', value === null ? 'Compteur VIP masqué' : `Places VIP → ${value}`);
    } catch {
      showToast('error', 'Erreur mise à jour places VIP');
    } finally {
      setVipSpotsSaving(false);
    }
  };

  const sendReminder = async (invoiceNumber, customMessage) => {
    const res = await post(`${BACKEND_URL}/api/admin/billing/send-reminder/${invoiceNumber}`, { custom_message: customMessage });
    if (res.status === 401) { onLogout(); return; }
    const data = await res.json();
    if (data.success) {
      showToast('success', `Rappel envoyé à ${data.email_sent_to}`);
      setReminderModal(null);
      loadAll();
    } else {
      showToast('error', data.detail || 'Erreur envoi rappel');
    }
  };

  return (
    <div data-testid="admin-billing-panel" className="space-y-5">
      {/* Sous-onglets */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
        {[
          { id: 'overview', label: 'Vue d\u0027ensemble', icon: TrendingUp },
          { id: 'invoices', label: 'Factures', icon: FileText },
          { id: 'reminders', label: `Relances${reminders.length ? ` (${reminders.length})` : ''}`, icon: Mail },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === t.id ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
              data-testid={`billing-subtab-${t.id}`}>
              <Ic className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filtre période */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-zinc-400 text-xs uppercase tracking-wider">Période</span>
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${period === p.id ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
              data-testid={`billing-period-${p.id}`}>{p.label}</button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={downloadCsv}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-semibold rounded-xl hover:scale-105 transition-transform"
          data-testid="billing-export-csv">
          <FileSpreadsheet className="w-4 h-4" /> Export comptabilité (.csv)
        </button>
      </div>

      {toast && (
        <div className={`p-3 rounded-xl text-sm flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'}`} data-testid="billing-toast">
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : (
        <>
          {tab === 'overview' && <OverviewView stats={stats} reminders={reminders} onGoReminders={() => setTab('reminders')} onLogout={onLogout}
            vipSpots={vipSpots} vipSpotsInput={vipSpotsInput} setVipSpotsInput={setVipSpotsInput}
            vipSpotsSaving={vipSpotsSaving} onSaveVipSpots={saveVipSpots} onGoToClients={onGoToClients} />}
          {tab === 'invoices' && (
            <InvoicesView
              invoices={invoices}
              search={search} setSearch={setSearch}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              fmtDate={fmtDate}
              onDownload={downloadInvoicePdf}
              onView={setDetailInvoice}
            />
          )}
          {tab === 'reminders' && (
            <RemindersView reminders={reminders} fmtDate={fmtDate} onRemind={setReminderModal} onDownload={downloadInvoicePdf} />
          )}
        </>
      )}

      {detailInvoice && <InvoiceDetailModal invoice={detailInvoice} onClose={() => setDetailInvoice(null)} onDownload={() => downloadInvoicePdf(detailInvoice.invoice_number)} fmtDate={fmtDate} />}
      {reminderModal && (
        <ReminderConfirmModal
          invoice={reminderModal}
          onCancel={() => setReminderModal(null)}
          onConfirm={(msg) => sendReminder(reminderModal.invoice_number, msg)}
        />
      )}
    </div>
  );
};

// ============================================================
// Sous-vues
// ============================================================
const OverviewView = ({ stats, reminders, onGoReminders, onLogout, vipSpots, vipSpotsInput, setVipSpotsInput, vipSpotsSaving, onSaveVipSpots, onGoToClients }) => {
  const [failedPayments, setFailedPayments] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/billing/failed-payments`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setFailedPayments(d.items || [])).catch(() => setFailedPayments([]));
  }, []);

  if (!stats) return null;
  return (
    <div className="space-y-5">
      {/* Bloc principal Revenue */}
      <div className="bg-gradient-to-br from-primary/20 via-pink-500/10 to-zinc-900 border border-primary/30 rounded-3xl p-6">
        <div className="text-zinc-300 text-sm uppercase tracking-wider mb-1">Revenu encaissé</div>
        <div className="text-5xl font-extrabold text-white" data-testid="billing-revenue-total">{stats.revenue_total_chf?.toFixed(2)} <span className="text-2xl text-zinc-400">CHF</span></div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-zinc-500 text-xs">Virement QR</div>
            <div className="text-white font-semibold">{stats.revenue_qr_chf?.toFixed(2)} CHF</div>
          </div>
          <div>
            <div className="text-zinc-500 text-xs">Stripe</div>
            <div className="text-white font-semibold">{stats.revenue_stripe_chf?.toFixed(2)} CHF</div>
          </div>
        </div>
      </div>

      {/* Graphique 12 mois glissants */}
      <RevenueChart onLogout={onLogout} />

      {/* Grille KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={CheckCircle2} color="#10B981" label="Factures payées" value={stats.invoices_paid_count} />
        <Kpi icon={Clock} color="#FBBF24" label="Factures en attente" value={stats.invoices_pending_count} sub={`${stats.pending_amount_chf?.toFixed(0)} CHF`} />
        <Kpi icon={CreditCard} color="#8B5CF6" label="Paiements Stripe" value={stats.stripe_paid_count} />
        <Kpi icon={AlertTriangle} color="#F97316" label="À relancer" value={stats.reminders_due_count}
             onClick={onGoReminders} highlight={stats.reminders_due_count > 0} />
      </div>

      {/* CTA Relances */}
      {reminders.length > 0 && (
        <button onClick={onGoReminders} className="w-full p-4 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 rounded-2xl flex items-center justify-between transition-colors text-left" data-testid="billing-cta-reminders">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <div>
              <div className="text-orange-300 font-semibold text-sm">{reminders.length} facture{reminders.length > 1 ? 's' : ''} en attente depuis 7+ jours</div>
              <div className="text-orange-200/70 text-xs">Vérifiez votre compte bancaire avant d&apos;envoyer un rappel</div>
            </div>
          </div>
          <Eye className="w-5 h-5 text-orange-400" />
        </button>
      )}

      {/* Paiements récurrents Stripe en échec — invisibles autrement */}
      {failedPayments && failedPayments.length > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4" data-testid="failed-payments-card">
          <h4 className="flex items-center gap-2 font-bold text-red-300 text-sm mb-3">
            <XCircle className="w-4 h-4" /> Paiements récurrents en échec ({failedPayments.length})
          </h4>
          <div className="space-y-1.5">
            {failedPayments.map((f) => (
              <button
                key={f.email}
                onClick={() => onGoToClients && onGoToClients(f.email)}
                className="w-full flex items-center justify-between gap-3 p-2.5 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                data-testid={`failed-payment-row-${f.email}`}
              >
                <span className="text-white text-sm">{f.email} <span className="text-zinc-500 text-xs">· {f.plan}</span></span>
                <span className="text-red-300 text-xs font-semibold">{f.days_since_failure != null ? `échec depuis ${f.days_since_failure}j` : 'échec récent'}</span>
              </button>
            ))}
          </div>
          <p className="text-red-200/60 text-[11px] mt-2">Stripe retentera automatiquement plusieurs fois. Relancez le client manuellement si besoin (mise à jour carte bancaire).</p>
        </div>
      )}

      {/* ===== Widget places VIP ===== */}
      <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/8 to-zinc-900/60 p-5" data-testid="vip-spots-widget">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.15em] text-purple-300 mb-1">Places VIP disponibles</p>
            <p className="text-sm text-zinc-400">Affiché sur la page <code className="text-purple-300">/transformation</code> pour créer l&apos;urgence</p>
          </div>
          <div className="shrink-0 text-right">
            {vipSpots !== null ? (
              <span className="text-2xl font-extrabold text-white">{vipSpots} <span className="text-sm text-zinc-400">place{vipSpots !== 1 ? 's' : ''}</span></span>
            ) : (
              <span className="text-sm text-zinc-500 italic">Badge masqué</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Boutons rapides -/+ */}
          <button onClick={() => onSaveVipSpots(Math.max(0, (vipSpots ?? 0) - 1))} disabled={vipSpotsSaving || vipSpots === null}
            className="w-8 h-8 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white font-bold flex items-center justify-center disabled:opacity-40 transition-colors"
            data-testid="vip-spots-decrement">−</button>
          <input type="number" min="0" max="20" value={vipSpotsInput}
            onChange={(e) => setVipSpotsInput(e.target.value)}
            onBlur={() => {
              const n = parseInt(vipSpotsInput, 10);
              if (!isNaN(n) && n >= 0 && n <= 20) onSaveVipSpots(n);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const n = parseInt(vipSpotsInput, 10);
                if (!isNaN(n) && n >= 0 && n <= 20) onSaveVipSpots(n);
              }
            }}
            className="w-16 text-center bg-zinc-800 border border-white/10 rounded-lg py-1.5 text-white text-sm focus:border-purple-400 focus:outline-none"
            data-testid="vip-spots-input" />
          <button onClick={() => onSaveVipSpots(Math.min(20, (vipSpots ?? 0) + 1))} disabled={vipSpotsSaving || vipSpots === null}
            className="w-8 h-8 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white font-bold flex items-center justify-center disabled:opacity-40 transition-colors"
            data-testid="vip-spots-increment">+</button>
          <button onClick={() => onSaveVipSpots(null)} disabled={vipSpotsSaving}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-400 border border-white/10 hover:border-white/25 hover:text-white transition-colors disabled:opacity-40"
            data-testid="vip-spots-hide">
            {vipSpots === null ? '✓ Masqué' : 'Masquer'}
          </button>
          {vipSpotsSaving && <span className="text-xs text-zinc-500">Sauvegarde…</span>}
        </div>
        <p className="text-[10px] text-zinc-600 mt-3">0 = &quot;COMPLET&quot; · 1 = &quot;Dernière place !&quot; · 2-3 = orange · 4+ = violet · Masqué = texte statique</p>
      </div>
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value, sub, onClick, highlight }) => (
  <div onClick={onClick}
       className={`bg-zinc-900 border rounded-2xl p-4 ${onClick ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''} ${highlight ? 'border-orange-500/40' : 'border-white/10'}`}>
    <Icon className="w-5 h-5 mb-2" style={{ color }} />
    <div className="text-2xl font-extrabold text-white">{value}</div>
    <div className="text-xs text-zinc-400 mt-1">{label}</div>
    {sub && <div className="text-zinc-500 text-[10px] mt-0.5">{sub}</div>}
  </div>
);

const InvoicesView = ({ invoices, search, setSearch, statusFilter, setStatusFilter, fmtDate, onDownload, onView }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (nom, email, numéro)…"
          className="w-full pl-10 pr-3 py-2 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:border-primary focus:outline-none text-sm"
          data-testid="billing-invoices-search" />
      </div>
      <div className="flex gap-1.5">
        {['', 'pending', 'paid'].map((f) => (
          <button key={f} onClick={() => setStatusFilter(f)}
            className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${statusFilter === f ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
            data-testid={`billing-status-${f || 'all'}`}>
            {f === '' ? 'Toutes' : f === 'pending' ? 'En attente' : 'Payées'}
          </button>
        ))}
      </div>
    </div>

    <div className="bg-zinc-900/40 border border-white/10 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-zinc-800/70 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              {['N°', 'Date', 'Client', 'Montant', 'Statut', 'Actions'].map((h) =>
                <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500 text-sm">Aucune facture sur cette période</td></tr>
            )}
            {invoices.map((i) => (
              <tr key={i.invoice_number} className="border-t border-white/5 text-zinc-300 hover:bg-white/5" data-testid={`billing-invoice-row-${i.invoice_number}`}>
                <td className="px-4 py-3 font-mono text-xs text-white">{i.invoice_number}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(i.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="text-white">{i.customer_name}</div>
                  <div className="text-zinc-500 text-xs">{i.customer_email}</div>
                </td>
                <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{i.total.toFixed(2)} CHF</td>
                <td className="px-4 py-3"><InvoiceStatusBadge status={i.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => onView(i)}
                      className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                      title="Voir détails" data-testid={`billing-view-${i.invoice_number}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onDownload(i.invoice_number)}
                      className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                      title="Télécharger PDF" data-testid={`billing-download-${i.invoice_number}`}>
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const RemindersView = ({ reminders, fmtDate, onRemind, onDownload }) => (
  <div className="space-y-3">
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex gap-3 items-start" data-testid="reminders-warning">
      <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="text-blue-200 text-sm">
        <strong className="text-blue-100">Important :</strong> Aucune relance n&apos;est envoyée automatiquement.
        <span className="text-blue-300/80"> Vérifiez d&apos;abord votre compte Raiffeisen — si le client a payé, marquez la facture comme payée
        depuis l&apos;onglet &quot;Paiements&quot; plutôt que d&apos;envoyer un rappel.</span>
      </div>
    </div>

    {reminders.length === 0 ? (
      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-8 text-center text-zinc-500">
        <CheckCircle2 className="w-10 h-10 text-green-500/60 mx-auto mb-3" />
        Aucune facture à relancer pour le moment.
      </div>
    ) : (
      <div className="space-y-2">
        {reminders.map((r) => (
          <div key={r.invoice_number} className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 flex flex-wrap gap-3 items-center" data-testid={`reminder-row-${r.invoice_number}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-white">{r.invoice_number}</span>
                <span className="text-zinc-500 text-xs">·</span>
                <span className="text-white text-sm">{r.customer_name}</span>
                <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-[10px] rounded-full font-semibold">{r.age_days}j</span>
                {r.reminders_count > 0 && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] rounded-full font-semibold">{r.reminders_count} rappel{r.reminders_count > 1 ? 's' : ''} envoyé{r.reminders_count > 1 ? 's' : ''}</span>
                )}
              </div>
              <div className="text-zinc-500 text-xs mt-1">{r.customer_email} · {fmtDate(r.created_at)}</div>
            </div>
            <div className="text-right">
              <div className="text-white font-bold text-lg leading-none">{r.total.toFixed(2)} CHF</div>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => onDownload(r.invoice_number)}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors" title="Télécharger PDF">
                <Download className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onRemind(r)}
                className="px-3 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-xs font-semibold rounded-lg hover:scale-105 transition-transform inline-flex items-center gap-1.5"
                data-testid={`send-reminder-${r.invoice_number}`}>
                <Mail className="w-3.5 h-3.5" /> Envoyer rappel
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ============================================================
// Modales
// ============================================================
const InvoiceStatusBadge = ({ status }) => {
  const cfg = status === 'paid'
    ? { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Payée', icon: CheckCircle2 }
    : { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'En attente', icon: Clock };
  const Ic = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Ic className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const InvoiceDetailModal = ({ invoice, onClose, onDownload, fmtDate }) => (
  <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="billing-invoice-detail-modal">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white">Facture {invoice.invoice_number}</h3>
          <InvoiceStatusBadge status={invoice.status} />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full"><X className="w-5 h-5 text-white" /></button>
      </div>

      <div className="space-y-3 text-sm">
        <Row label="Client" value={invoice.customer_name} />
        <Row label="Email" value={invoice.customer_email} />
        {invoice.customer_city && <Row label="Ville" value={invoice.customer_city} />}
        <Row label="Émise le" value={fmtDate(invoice.created_at)} />
        {invoice.paid_at && <Row label="Payée le" value={fmtDate(invoice.paid_at)} />}
        {invoice.admin_note && <Row label="Note interne" value={invoice.admin_note} />}
        {invoice.marked_paid_by && <Row label="Marquée par" value={invoice.marked_paid_by} />}
      </div>

      {invoice.items && invoice.items.length > 0 && (
        <div className="mt-4 bg-white/5 rounded-xl p-3">
          {invoice.items.map((it, idx) => (
            <div key={idx} className="flex justify-between text-sm py-1">
              <span className="text-zinc-300">{it.description}</span>
              <span className="text-white font-semibold">{(it.total || 0).toFixed(2)} CHF</span>
            </div>
          ))}
          <div className="border-t border-white/10 mt-2 pt-2 flex justify-between">
            <span className="text-zinc-400 font-semibold">Total</span>
            <span className="text-white font-bold text-lg">{invoice.total.toFixed(2)} CHF</span>
          </div>
        </div>
      )}

      <button onClick={onDownload} className="mt-5 w-full py-2.5 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl font-semibold inline-flex items-center justify-center gap-2 hover:scale-105 transition-transform">
        <Download className="w-4 h-4" /> Télécharger le PDF
      </button>
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-zinc-500">{label}</span>
    <span className="text-white text-right">{value || '—'}</span>
  </div>
);

const ReminderConfirmModal = ({ invoice, onCancel, onConfirm }) => {
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => { setLoading(true); await onConfirm(msg || null); };

  return (
    <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="reminder-confirm-modal">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Envoyer un rappel manuel</h3>
            <p className="text-zinc-400 text-sm">{invoice.customer_name} · {invoice.total.toFixed(2)} CHF</p>
          </div>
        </div>

        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 mb-4 text-xs text-orange-200">
          ⚠️ Avez-vous vérifié votre compte Raiffeisen ? Si le client a déjà payé, marquez-le comme payé dans l&apos;onglet &quot;Paiements&quot; plutôt que d&apos;envoyer un rappel.
        </div>

        <div className="mb-4">
          <label className="block text-zinc-300 text-xs font-semibold mb-2">Message personnalisé (optionnel)</label>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
            placeholder="Laisser vide pour utiliser le message par défaut…"
            className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-xl text-white text-sm focus:border-primary focus:outline-none"
            data-testid="reminder-custom-message" />
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={loading}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm disabled:opacity-50">
            Annuler
          </button>
          <button onClick={submit} disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl text-sm font-semibold inline-flex items-center gap-2 hover:scale-105 transition-transform disabled:opacity-50"
            data-testid="reminder-confirm-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Envoyer le rappel
          </button>
        </div>
      </div>
    </div>
  );
};
